import moment from "moment";
import { ItemAggregate, GroupAggregate, PivotResult, DisplayType, Totals } from "./types";
import { organizationStore } from "../../../../store/organization";
import { eventStore } from "../../../../store/event";
import { reportFiltersStore } from "../../../../store/reportFilters";

const escape = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const dollars = (cents: number) => (cents / 100).toFixed(2);

const buildHeaderRows = (searchString: string): string[][] => {
  const rows: string[][] = [];
  rows.push(["Product Mix Report (Updated)"]);
  rows.push([`Exported: ${moment().format("ddd, MMM DD, YYYY h:mm a")}`]);

  const orgState = organizationStore.getState();
  const eventState = eventStore.getState();
  const filtersState = reportFiltersStore.getState();
  const allEvents: any[] = eventState.allEvents || [];
  const allOrgs: any[] = orgState.allOrganizations || orgState.organizations || [];
  const allVendors: any[] = filtersState.allVendors || [];
  const allLocations: any[] = filtersState.allLocations || [];

  const filters: string[] = [];
  const search = new URLSearchParams(searchString);
  const where = search.get("where");
  if (where) {
    try {
      const w = JSON.parse(where);
      const dateFrom = w?._and?.[0]?.transaction_hour?._gte;
      const dateTo = w?._and?.[1]?.transaction_hour?._lt;
      if (dateFrom) filters.push(`Date From: ${moment(dateFrom).format("MMM DD, YYYY h:mm a")}`);
      if (dateTo) filters.push(`Date To: ${moment(dateTo).format("MMM DD, YYYY h:mm a")}`);

      const lookup = (arr: any[], id: any) => arr.find((x) => String(x.id) === String(id))?.name ?? id;
      if (w?.event_id?._eq) filters.push(`Event: ${lookup(allEvents, w.event_id._eq)}`);
      if (w?.vendor_id?._in) filters.push(`Vendors: ${w.vendor_id._in.map((id: any) => lookup(allVendors, id)).join(", ")}`);
      if (w?.location_id?._in) filters.push(`Locations: ${w.location_id._in.map((id: any) => lookup(allLocations, id)).join(", ")}`);
      if (w?.username?._in) filters.push(`Usernames: ${w.username._in.join(", ")}`);
      if (w?.organization_id?._eq) filters.push(`Organization: ${lookup(allOrgs, w.organization_id._eq)}`);
    } catch {
      // ignore
    }
  }
  rows.push([filters.length > 0 ? `Filters: ${filters.join(" | ")}` : "Filters: None"]);
  rows.push([""]);
  return rows;
};

const itemColumns: { header: string; getter: (i: ItemAggregate) => string | number }[] = [
  { header: "Product Name", getter: (i) => i.posName },
  { header: "Vendor", getter: (i) => i.vendorName },
  { header: "Product Type", getter: (i) => i.productType },
  { header: "Description", getter: (i) => i.description },
  { header: "UPC", getter: (i) => i.upc },
  { header: "GL Account Name", getter: (i) => i.glAccountName },
  { header: "GL Account Number", getter: (i) => i.glAccountNumber },
  { header: "Item Price", getter: (i) => dollars(i.itemPriceCents) },
  { header: "Total Units Sold", getter: (i) => i.totalUnitsSold },
  { header: "Cash Units", getter: (i) => i.cashUnitsSold },
  { header: "Cash Balance Units", getter: (i) => i.cashBalanceUnitsSold },
  { header: "Credit Units", getter: (i) => i.creditUnitsSold },
  { header: "RFID Units", getter: (i) => i.rfidUnitsSold },
  { header: "Token Units", getter: (i) => i.tokenUnitsSold },
  { header: "Gross Sales", getter: (i) => dollars(i.totalGrossCents) },
  { header: "Tax", getter: (i) => dollars(i.totalTaxCents) },
  { header: "Discounts", getter: (i) => dollars(i.totalDiscountsCents) },
  { header: "Promo Discounts", getter: (i) => dollars(i.totalPromoDiscountsCents) },
  { header: "Refunds", getter: (i) => dollars(i.totalRefundsCents) },
  { header: "Net Sales", getter: (i) => dollars(i.totalNetCents) },
];

const totalsRow = (t: Totals): string[] => [
  "TOTAL",
  "",
  "",
  "",
  "",
  "",
  "",
  "",
  String(t.totalUnitsSold),
  String(t.cashUnitsSold),
  String(t.cashBalanceUnitsSold),
  String(t.creditUnitsSold),
  String(t.rfidUnitsSold),
  String(t.tokenUnitsSold),
  dollars(t.totalGrossCents),
  dollars(t.totalTaxCents),
  dollars(t.totalDiscountsCents),
  dollars(t.totalPromoDiscountsCents),
  dollars(t.totalRefundsCents),
  dollars(t.totalNetCents),
];

const itemAsRow = (item: ItemAggregate, prefix: string[] = []): string[] => [
  ...prefix,
  ...itemColumns.map((c) => String(c.getter(item))),
];

const flatItemRows = (items: ItemAggregate[], totals: Totals): string[][] => {
  const headers = itemColumns.map((c) => c.header);
  const rows = [headers, ...items.map((i) => itemAsRow(i))];
  rows.push(totalsRow(totals));
  return rows;
};

const groupedRows = (groups: GroupAggregate[], groupLabel: string, totals: Totals, depth = 1): string[][] => {
  const headers = [...Array(depth).fill(groupLabel), ...itemColumns.map((c) => c.header)];
  const rows: string[][] = [headers];
  const walk = (group: GroupAggregate, prefix: string[]) => {
    const groupPrefix = [...prefix, group.label];
    if (group.subgroups && group.subgroups.length > 0) {
      group.subgroups.forEach((sub) => walk(sub, groupPrefix));
    }
    group.items.forEach((item) => {
      rows.push(itemAsRow(item, padPrefix(groupPrefix, depth)));
    });
  };
  groups.forEach((g) => walk(g, []));
  rows.push([...Array(depth).fill(""), ...totalsRow(totals).slice(0)]);
  return rows;
};

const padPrefix = (prefix: string[], depth: number): string[] => {
  const out = [...prefix];
  while (out.length < depth) out.push("");
  return out;
};

const pivotRows = (pivot: PivotResult): string[][] => {
  const headers = [pivot.axisLabel];
  pivot.itemNames.forEach((n) => {
    headers.push(`${n} - Units`);
    headers.push(`${n} - Gross`);
  });
  const rows: string[][] = [headers];
  pivot.rows.forEach((row) => {
    const out = [row.label];
    pivot.itemNames.forEach((name) => {
      const c = row.cells[name];
      out.push(c ? String(c.units) : "0");
      out.push(c ? dollars(c.grossCents) : "0.00");
    });
    rows.push(out);
  });
  const total = ["TOTAL"];
  pivot.itemNames.forEach((name) => {
    const t = pivot.itemTotals[name];
    total.push(String(t.units));
    total.push(dollars(t.grossCents));
  });
  rows.push(total);
  return rows;
};

interface ExportArgs {
  displayType: DisplayType;
  searchString: string;
  itemView?: { items: ItemAggregate[]; totals: Totals };
  groupView?: { groups: GroupAggregate[]; totals: Totals; depth: number; groupLabel: string };
  pivot?: PivotResult;
}

export const exportProductMixCsv = (args: ExportArgs) => {
  const rows: string[][] = buildHeaderRows(args.searchString);

  if (args.pivot) {
    rows.push(...pivotRows(args.pivot));
  } else if (args.groupView) {
    rows.push(...groupedRows(args.groupView.groups, args.groupView.groupLabel, args.groupView.totals, args.groupView.depth));
  } else if (args.itemView) {
    rows.push(...flatItemRows(args.itemView.items, args.itemView.totals));
  }

  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  a.download = `ProductMixReport_${args.displayType}_${timestamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
